import { SMSContext } from 'admin/features/clients/viewClientDetails/SMSClientTab';
import useGoToUserProfile from 'app/hooks/useGoToUserProfile';
import useIsOverflow from 'app/hooks/useIsOverflow';
import { IUser } from 'app/services/user';
import Tooltip from 'Components/Tooltips';
import UserAvatar from 'Components/UserAvatar';
import React, { forwardRef, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'utils/AuthProvider';

interface UserBoxProps {
  user: IUser;
  right?: React.ReactNode;
  content?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  theme?: 'gray' | 'green' | 'white' | '';
  px?: '' | '1' | '2' | '4' | '6';
  py?: '' | '1' | '2' | '4' | '6';
  className?: string;
  onClick?: () => void;
  my?: '' | '1.5';
  shadow?: '' | 'shadow';
  hover?: string;
  border?: string;
}

const UserBox = forwardRef(
  (
    {
      user,
      right,
      content,
      border,
      className,
      size = 'xs',
      theme = 'gray',
      px = '2',
      py = '2',
      onClick,
      my = '1.5',
      shadow = 'shadow',
      hover = '',
    }: UserBoxProps,
    ref: any,
  ) => {
    const ltBg =
      theme === 'green'
        ? 'bg-green-light bg-opacity-30'
        : theme === 'gray'
        ? 'bg-gray-light-10'
        : theme === 'white'
        ? 'bg-white'
        : '';

    let { showPhoneNumber, phoneNumber } = React.useContext(SMSContext);
    const nameOverflowRef = useRef<HTMLDivElement>(null);
    const emailOverflowRef = useRef<HTMLDivElement>(null);
    const isNameOverflown = useIsOverflow(nameOverflowRef);
    const isEmailOverflown = useIsOverflow(nameOverflowRef);
    const navigateToProfile = useGoToUserProfile();
    const gotoUserProfile = () => navigateToProfile(user);

    if (theme === '') {
      px = '';
      py = '';
      shadow = '';
      my = '';
    }
    return (
      <div
        ref={ref}
        className={`my-${my} ${hover} ${shadow} ${border} rounded-xl px-${px} py-${py} text-gray-dark text-${size} ${ltBg} ${className} overflow-hidden`}
      >
        <div className={`my-1 flex items-center justify-between `}>
          <div className="flex flex-1">
            <div>
              <Tooltip message="View Profile">
                <UserAvatar
                  user={user}
                  size={size === 'xs' ? 'w-7 h-7 text-xs  ' : undefined}
                  theme={theme === 'green' ? 'green' : 'gray'}
                  className="self-start mr-2 cursor-pointer"
                  onClick={gotoUserProfile}
                />
              </Tooltip>
            </div>

            <div
              className={`flex flex-col text-dark-gray md-max:text-xs justify-center`}
            >
              <Tooltip
                message={
                  isNameOverflown
                    ? `${user?.given_name} ${user?.family_name}`
                    : ''
                }
              >
                <span
                  ref={nameOverflowRef}
                  className="font-bold capitalize line-clamp-1"
                >
                  {user?.given_name} {user?.family_name}
                </span>
              </Tooltip>

              <Tooltip
                message={
                  isEmailOverflown
                    ? `${showPhoneNumber ? user?.sender_number : user?.email}`
                    : ''
                }
              >
                <span
                  ref={emailOverflowRef}
                  className="text-gray-700 normal-case line-clamp-1"
                >
                  {showPhoneNumber ? user?.sender_number : user?.email}
                </span>
              </Tooltip>
            </div>
          </div>

          {right}
        </div>

        {content}
      </div>
    );
  },
);

export default UserBox;
